import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import {
  getActiveMerchantUuid,
  getContextMerchantUuid,
  getContextToken,
  getGroupToken,
  isGroupMode
} from 'lib/merchant-group'
import { parseCookies } from 'nookies'
export const baseURL =
  process.env.NEXT_PUBLIC_API_BASE_URL || 'https://api.clientbase.com.br'

/**
 * SC-10999: para onde vai a chamada na visão multi-empresas.
 * - `scope: 'group'`: token de grupo (só leitura agregada: listas, KPIs,
 *   métricas, busca e relatórios).
 * - `merchantUuid`: token daquela empresa do grupo.
 * Sem nenhum dos dois, vale a empresa ativa (modal/formulário aberto) e, sem
 * ela, o `nextauth.token` de sempre. Fora da visão do grupo nada muda.
 */
export type RequestScope = {
  scope?: 'group'
  merchantUuid?: string
}

export type MerchantTokenInfo = {
  token: string
  merchant?: {
    merchant_uuid: string
    uuid?: string
    name: string
    logo_url?: string | null
  }
  merchant_user_uuid?: string
  role?: string
  permissions?: string
  creation_blocked?: boolean
  current_plan?: string
  // Payload do login dessa empresa (Merchant::Encoder.to_json_auth): o que os
  // formulários da visão multi-empresas leem em vez dos cookies da sessão.
  session?: Record<string, any>
}

// Tokens das empresas do grupo, trocados pelo token de grupo na primeira vez
// que a empresa é usada. Só em memória: o hard reload da troca de conta e do
// logout descarta tudo junto.
const merchantTokens = new Map<string, MerchantTokenInfo>()
const pendingMerchantTokens = new Map<string, Promise<MerchantTokenInfo>>()

export const clearMerchantTokens = () => {
  merchantTokens.clear()
  pendingMerchantTokens.clear()
}

/** Tokens de empresa emitidos nesta sessão (revogados no logout). */
export const listMerchantTokens = () =>
  Array.from(merchantTokens.values())
    .map((info) => info.token)
    .filter(Boolean)

export const getCachedMerchantToken = (merchantUuid?: string) =>
  merchantUuid ? merchantTokens.get(merchantUuid) : undefined

export const fetchMerchantToken = (
  merchantUuid: string,
  { force = false }: { force?: boolean } = {}
): Promise<MerchantTokenInfo> => {
  if (force) merchantTokens.delete(merchantUuid)

  const cached = merchantTokens.get(merchantUuid)
  if (cached) return Promise.resolve(cached)

  const pending = pendingMerchantTokens.get(merchantUuid)
  if (pending) return pending

  const request = axios
    .post<MerchantTokenInfo>(
      `${baseURL}/v1/auth/merchant_token`,
      { merchant_uuid: merchantUuid },
      { headers: { Authorization: `Bearer ${getGroupToken()}` } }
    )
    .then((response) => {
      merchantTokens.set(merchantUuid, response.data)
      return response.data
    })
    .finally(() => pendingMerchantTokens.delete(merchantUuid))

  pendingMerchantTokens.set(merchantUuid, request)
  return request
}

type ResolvedToken = { token?: string; merchantUuid?: string }

const resolveToken = async (
  ctx: any,
  { scope, merchantUuid }: RequestScope,
  force = false
): Promise<ResolvedToken> => {
  const contextToken = getContextToken(ctx)

  if (!isGroupMode(ctx)) return { token: contextToken }

  if (scope === 'group') return { token: getGroupToken(ctx) || contextToken }

  // Empresa ativa e tokens de empresa só existem no navegador: o cache é do
  // módulo e, no servidor, seria compartilhado entre usuários. No SSR vale
  // sempre o token de contexto.
  if (typeof window === 'undefined') return { token: contextToken }

  const target = merchantUuid || getActiveMerchantUuid()

  if (!target || target === getContextMerchantUuid(ctx)) {
    return { token: contextToken }
  }

  const info = await fetchMerchantToken(target, { force })
  return { token: info.token, merchantUuid: target }
}

type RoutedConfig = AxiosRequestConfig & {
  __cbMerchantUuid?: string
  __cbRetried?: boolean
}

export function getAPIClient(ctx?: any, requestScope: RequestScope = {}) {
  const { ['nextauth.token']: token } = parseCookies(ctx)

  const api = axios.create({ baseURL })

  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`
  }

  // Fora da visão do grupo o client é exatamente o de antes.
  if (!isGroupMode(ctx)) return api

  api.interceptors.request.use(async (config: RoutedConfig) => {
    const resolved = await resolveToken(ctx, requestScope, !!config.__cbRetried)

    config.headers = config.headers || {}
    if (resolved.token) {
      config.headers.Authorization = `Bearer ${resolved.token}`
    }
    config.__cbMerchantUuid = resolved.merchantUuid

    return config
  })

  // Token de empresa expirado ou revogado: busca um novo uma vez antes de
  // devolver o 401 para quem chamou.
  api.interceptors.response.use(undefined, (error: AxiosError) => {
    const config = error.config as RoutedConfig | undefined

    if (
      error.response?.status === 401 &&
      config?.__cbMerchantUuid &&
      !config.__cbRetried
    ) {
      config.__cbRetried = true
      return api(config)
    }

    return Promise.reject(error)
  })

  return api
}
